/**
 * Typed client for the portal API (docs/PORTAL-PLAN.md 5.2, src/portal/contract.ts).
 *
 * Rules that live here and nowhere else:
 *  - the session is the HttpOnly cookie; this module never reads or stores it
 *  - the CSRF token returned by /api/me (or the verify response) is held in memory only,
 *    never in web storage, and is sent as X-Portal-CSRF on non-GET requests under /api
 *    and /auth/logout*. The pre-login /auth/code/* calls do not use it (5.2, 4.5)
 *  - codes and addresses travel in JSON bodies, never in URLs or logs
 *  - every non-GET sends Content-Type: application/json (the server checks it)
 */

import {
  CSRF_HEADER,
  PORTAL_ROUTES,
  type CodeSentResponse,
  type InvoiceDetail,
  type InvoiceListQuery,
  type Lang,
  type MeResponse,
  type OverviewResponse,
  type Page,
  type InvoiceSummary,
  type Payment,
  type PaymentListQuery,
  type PreferencesResponse,
  type Statement,
  type StatementQuery,
  type StatusResponse,
  type VerifyResponse,
} from '@portal-contract';
import { ApiClientError } from './errors';

let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null): void => {
  csrfToken = t;
};

type Query = Record<string, string | number | undefined>;

interface RequestOptions {
  body?: unknown;
  query?: Query;
  /** Pre-login endpoints never carry the CSRF header. */
  csrf?: boolean;
  signal?: AbortSignal | undefined;
  accept?: string;
}

function fill(template: string, params: Record<string, string>): string {
  return template.replace(/:(\w+)/g, (_, k: string) => encodeURIComponent(params[k] ?? ''));
}

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `${path}?${s}` : path;
}

async function send(method: 'GET' | 'POST' | 'PUT', path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: opts.accept ?? 'application/json' };
  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    signal: opts.signal ?? null,
  };
  if (method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body ?? {});
    if (opts.csrf !== false && csrfToken) headers[CSRF_HEADER] = csrfToken;
  }
  try {
    return await fetch(withQuery(path, opts.query), init);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiClientError(0, 'network');
  }
}

async function readJson(res: Response): Promise<unknown> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('json')) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(method: 'GET' | 'POST' | 'PUT', path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(method, path, opts);
  if (!res.ok) throw ApiClientError.fromResponse(res.status, await readJson(res));
  if (res.status === 204) return undefined as T;
  return (await readJson(res)) as T;
}

export const api = {
  status: (signal?: AbortSignal) => request<StatusResponse>('GET', PORTAL_ROUTES.status, { signal }),

  requestCode: (email: string, lang: Lang) =>
    request<CodeSentResponse>('POST', PORTAL_ROUTES.codeRequest, { body: { email, lang }, csrf: false }),
  resendCode: () => request<CodeSentResponse>('POST', PORTAL_ROUTES.codeResend, { body: {}, csrf: false }),
  verifyCode: (code: string) => request<VerifyResponse>('POST', PORTAL_ROUTES.codeVerify, { body: { code }, csrf: false }),

  me: (signal?: AbortSignal) => request<MeResponse>('GET', PORTAL_ROUTES.me, { signal }),
  setLanguage: (lang: Lang) => request<PreferencesResponse>('PUT', PORTAL_ROUTES.preferences, { body: { lang } }),
  logout: () => request<void>('POST', PORTAL_ROUTES.logout),
  logoutAll: () => request<void>('POST', PORTAL_ROUTES.logoutAll),

  overview: (cid: string, signal?: AbortSignal) =>
    request<OverviewResponse>('GET', fill(PORTAL_ROUTES.overview, { cid }), { signal }),
  invoices: (cid: string, q: InvoiceListQuery, signal?: AbortSignal) =>
    request<Page<InvoiceSummary>>('GET', fill(PORTAL_ROUTES.invoices, { cid }), { query: { ...q }, signal }),
  invoice: (cid: string, iid: string, signal?: AbortSignal) =>
    request<InvoiceDetail>('GET', fill(PORTAL_ROUTES.invoice, { cid, iid }), { signal }),
  payments: (cid: string, q: PaymentListQuery, signal?: AbortSignal) =>
    request<Page<Payment>>('GET', fill(PORTAL_ROUTES.payments, { cid }), { query: { ...q }, signal }),
  statement: (cid: string, q: StatementQuery, signal?: AbortSignal) =>
    request<Statement>('GET', fill(PORTAL_ROUTES.statement, { cid }), { query: { ...q }, signal }),

  /** Fetches a PDF as a Blob (errors are JSON, so a failed download never navigates the page). */
  async invoicePdf(cid: string, iid: string): Promise<Blob> {
    return pdfBlob(fill(PORTAL_ROUTES.invoicePdf, { cid, iid }));
  },
  async statementPdf(cid: string, q: StatementQuery): Promise<Blob> {
    return pdfBlob(fill(PORTAL_ROUTES.statementPdf, { cid }), { ...q });
  },
};

async function pdfBlob(path: string, query?: Query): Promise<Blob> {
  const res = await send('GET', path, { accept: 'application/pdf, application/json', ...(query ? { query } : {}) });
  if (!res.ok) throw ApiClientError.fromResponse(res.status, await readJson(res));
  return res.blob();
}

/** Saves a blob through a temporary anchor. The object URL is revoked right after the click. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
